import { CELESTIAL_BODY_SPECS, STAR_COLLISION_RADIUS } from '../simulation/celestial-system';
import type { RendererState } from './renderer-state';

const INITIAL_ZOOM = 0.42;
const FOCUS_ZOOM = 5.5;
const MINIMUM_ZOOM = 0.12;
// Instanced quads have no point-size limit; retain the intended close-up scale.
const MAXIMUM_ZOOM = 5.5;
const TRANSITION_SECONDS = 0.65;
const MINIMUM_PICK_RADIUS_CSS = 28;
const FOCUS_BODIES = [{ index: 0, bodyRadius: STAR_COLLISION_RADIUS }, ...CELESTIAL_BODY_SPECS];

export interface CameraFrame {
  readonly centerX: number;
  readonly centerY: number;
  readonly zoom: number;
  readonly moving: boolean;
}

export class PlanetCamera {
  private selectedPlanetIndex: number | null = null;
  private focusRequested = false;
  private progress = 0;
  private targetZoom = INITIAL_ZOOM;
  private systemZoomBeforeFocus = INITIAL_ZOOM;
  private systemCenterX = 0;
  private systemCenterY = 0;
  private frame: CameraFrame = {
    centerX: 0,
    centerY: 0,
    zoom: INITIAL_ZOOM,
    moving: false,
  };

  public get mode(): string {
    return this.focusRequested && this.selectedPlanetIndex !== null
      ? `body-${this.selectedPlanetIndex}`
      : 'solar-system';
  }

  public get currentFrame(): CameraFrame {
    return this.frame;
  }

  public toggleAt(
    clipX: number,
    clipY: number,
    cssWidth: number,
    cssHeight: number,
    state: RendererState,
    interpolation: number,
  ): boolean {
    if (this.focusRequested && this.selectedPlanetIndex !== null) {
      this.focusRequested = false;
      this.targetZoom = this.systemZoomBeforeFocus;
      return true;
    }

    const projection = projectionScale(cssWidth, cssHeight);

    let closestIndex: number | null = null;
    let closestDistanceSquared = Number.POSITIVE_INFINITY;
    for (let slot = 0; slot < FOCUS_BODIES.length; slot += 1) {
      const planet = FOCUS_BODIES[slot];
      if (planet === undefined || planet.index >= state.count) {
        continue;
      }
      const position = interpolatedPosition(state, planet.index, interpolation);
      const planetClipX =
        (position.x - this.frame.centerX) * this.frame.zoom * projection.x;
      const planetClipY =
        (position.y - this.frame.centerY) * this.frame.zoom * projection.y;
      const dx = (clipX - planetClipX) * Math.max(1, cssWidth) * 0.5;
      const dy = (clipY - planetClipY) * Math.max(1, cssHeight) * 0.5;
      const distanceSquared = dx * dx + dy * dy;
      const radius = Math.max(
        MINIMUM_PICK_RADIUS_CSS,
        planet.bodyRadius * Math.min(cssWidth, cssHeight) * this.frame.zoom * 0.5,
      );
      if (
        distanceSquared <= radius * radius &&
        distanceSquared < closestDistanceSquared
      ) {
        closestIndex = planet.index;
        closestDistanceSquared = distanceSquared;
      }
    }
    if (closestIndex === null) {
      return false;
    }
    this.selectedPlanetIndex = closestIndex;
    this.focusRequested = true;
    this.systemZoomBeforeFocus = this.targetZoom;
    this.targetZoom = Math.max(FOCUS_ZOOM, this.targetZoom);
    return true;
  }

  public zoomByWheel(deltaY: number): void {
    const factor = Math.exp(-Math.max(-600, Math.min(600, deltaY)) * 0.0015);
    this.targetZoom = Math.max(
      MINIMUM_ZOOM,
      Math.min(MAXIMUM_ZOOM, this.targetZoom * factor),
    );
    if (!this.focusRequested) {
      this.systemZoomBeforeFocus = this.targetZoom;
    }
  }

  /** Keep the world point under the moving pinch midpoint fixed to the fingers. */
  public pinchByPixels(ratio: number, fromX: number, fromY: number,
    toX: number, toY: number, width: number, height: number): void {
    if (!Number.isFinite(ratio) || ratio <= 0) return;
    const projection = projectionScale(width, height);
    const zoom = Math.max(MINIMUM_ZOOM, Math.min(MAXIMUM_ZOOM, this.frame.zoom * ratio));
    const clipX = (x: number) => (2 * x / Math.max(1, width) - 1) / projection.x;
    const clipY = (y: number) => (1 - 2 * y / Math.max(1, height)) / projection.y;
    this.systemCenterX = this.frame.centerX + clipX(fromX) / this.frame.zoom - clipX(toX) / zoom;
    this.systemCenterY = this.frame.centerY + clipY(fromY) / this.frame.zoom - clipY(toY) / zoom;
    this.selectedPlanetIndex = null;
    this.focusRequested = false;
    this.progress = 0;
    this.targetZoom = this.systemZoomBeforeFocus = zoom;
    this.frame = { centerX: this.systemCenterX, centerY: this.systemCenterY, zoom, moving: true };
  }

  public panByPixels(
    deltaX: number,
    deltaY: number,
    cssWidth: number,
    cssHeight: number,
  ): void {
    const projection = projectionScale(cssWidth, cssHeight);
    if (this.focusRequested) {
      this.systemCenterX = this.frame.centerX;
      this.systemCenterY = this.frame.centerY;
      this.selectedPlanetIndex = null;
      this.focusRequested = false;
      this.progress = 0;
      this.targetZoom = this.frame.zoom;
      this.systemZoomBeforeFocus = this.targetZoom;
    }
    this.systemCenterX -=
      (deltaX * 2) /
      (Math.max(1, cssWidth) * this.frame.zoom * projection.x);
    this.systemCenterY +=
      (deltaY * 2) /
      (Math.max(1, cssHeight) * this.frame.zoom * projection.y);
    this.frame = {
      centerX: this.systemCenterX,
      centerY: this.systemCenterY,
      zoom: this.frame.zoom,
      moving: true,
    };
  }

  public update(
    deltaSeconds: number,
    state: RendererState,
    interpolation: number,
  ): CameraFrame {
    const previousProgress = this.progress;
    const previousZoom = this.frame.zoom;
    const step = Math.max(0, deltaSeconds) / TRANSITION_SECONDS;
    this.progress = this.focusRequested
      ? Math.min(1, this.progress + step)
      : Math.max(0, this.progress - step);

    if (!this.focusRequested && this.progress <= 0) {
      this.selectedPlanetIndex = null;
      const zoomBlend = 1 - Math.exp(-Math.max(0, deltaSeconds) * 10);
      const zoom = previousZoom + (this.targetZoom - previousZoom) * zoomBlend;
      this.frame = {
        centerX: this.systemCenterX,
        centerY: this.systemCenterY,
        zoom,
        moving: Math.abs(zoom - previousZoom) > 1e-5,
      };
      return this.frame;
    }

    if (
      this.selectedPlanetIndex === null ||
      this.selectedPlanetIndex >= state.count
    ) {
      this.selectedPlanetIndex = null;
      this.focusRequested = false;
      this.progress = 0;
      this.targetZoom = INITIAL_ZOOM;
      this.systemCenterX = 0;
      this.systemCenterY = 0;
      this.frame = { centerX: 0, centerY: 0, zoom: INITIAL_ZOOM, moving: false };
      return this.frame;
    }

    const position = interpolatedPosition(
      state,
      this.selectedPlanetIndex,
      interpolation,
    );
    const eased = smoothstep(this.progress);
    const zoomBlend = 1 - Math.exp(-Math.max(0, deltaSeconds) * 10);
    const zoom = previousZoom + (this.targetZoom - previousZoom) * zoomBlend;
    this.frame = {
      centerX:
        this.systemCenterX + (position.x - this.systemCenterX) * eased,
      centerY:
        this.systemCenterY + (position.y - this.systemCenterY) * eased,
      zoom,
      moving:
        Math.abs(this.progress - previousProgress) > 1e-6 ||
        Math.abs(zoom - previousZoom) > 1e-5,
    };
    return this.frame;
  }

  public reset(): void {
    this.selectedPlanetIndex = null;
    this.focusRequested = false;
    this.progress = 0;
    this.targetZoom = INITIAL_ZOOM;
    this.systemZoomBeforeFocus = INITIAL_ZOOM;
    this.systemCenterX = 0;
    this.systemCenterY = 0;
    this.frame = { centerX: 0, centerY: 0, zoom: INITIAL_ZOOM, moving: false };
  }
}

function interpolatedPosition(
  state: RendererState,
  index: number,
  interpolation: number,
): { readonly x: number; readonly y: number } {
  const blend = Math.max(0, Math.min(1, interpolation));
  const previousX = state.previousX[index] ?? state.currentX[index] ?? 0;
  const previousY = state.previousY[index] ?? state.currentY[index] ?? 0;
  const currentX = state.currentX[index] ?? previousX;
  const currentY = state.currentY[index] ?? previousY;
  return {
    x: previousX + (currentX - previousX) * blend,
    y: previousY + (currentY - previousY) * blend,
  };
}

function smoothstep(value: number): number {
  return value * value * (3 - 2 * value);
}

export function projectionScale(
  cssWidth: number,
  cssHeight: number,
): { readonly x: number; readonly y: number } {
  const width = Math.max(1, cssWidth);
  const height = Math.max(1, cssHeight);
  return {
    x: Math.min(1, height / width),
    y: Math.min(1, width / height),
  };
}
